// my current oral assessment page

import React, { useEffect, useRef, useState } from "react";
import Layout from "../Layout";
import { Button, Card, Col, Divider, message, Modal, Row, Spin, Progress } from "antd";
import { AudioOutlined, RedoOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import "./OralAssessment.css";
import { motion, AnimatePresence } from "framer-motion";
import {
    Play,
    Square,
    RefreshCw,
    Volume2,
    Book,
    CheckCircle,
    XCircle,
    MessageSquare,
    Zap
} from "lucide-react";
import { API_CONFIG } from "../config/api";

const OralAssessment = () => {
    const [books, setBooks] = useState([]);
    const [selectedBook, setSelectedBook] = useState(null);
    const [topics, setTopics] = useState([]);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [questionIndices, setQuestionIndices] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [audioSrc, setAudioSrc] = useState(null);
    const [audioPlaying, setAudioPlaying] = useState(false);
    const [recording, setRecording] = useState(false);
    const [audioFile, setAudioFile] = useState(null);
    const [feedback, setFeedback] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [quizVisible, setQuizVisible] = useState(false); // Control Quiz Modal
    const [isRecording, setIsRecording] = useState(false);
    const [isLoadingQuestion, setIsLoadingQuestion] = useState(true);

    const server_url = API_CONFIG.BASE_URL;
    const audioRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);

    const audioBlob = new Blob(audioChunksRef.current, { type: "audio/mp3" });

    const accessToken = localStorage.getItem("accessToken");


    const [quizStarted, setQuizStarted] = useState(false);
    const [quizEnded, setQuizEnded] = useState(false);


    useEffect(() => {
        fetchBooks();
    }, []);

    // Fetch Books
    const fetchBooks = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${server_url}/books`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const data = await response.json();
            setBooks(data.books);
        } catch (error) {
            console.error("Error fetching books:", error);
        }
        setLoading(false);
    };

    // Fetch Topics for Selected Book
    const fetchTopics = async (bookId) => {
        if (!bookId || (selectedBook?.book_id === bookId && topics.length > 0)) return;
        setLoading(true);

        try {
            const response = await fetch(`${server_url}/books/${bookId}/topics`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const data = await response.json();
            setTopics(data.topics);
            setSelectedBook(bookId);
        } catch (error) {
            console.error("Error fetching topics:", error);
        }

        setLoading(false);
    };

    // Fetch Question Indices
    const fetchQuestionIndices = async (topicId) => {
        if (!topicId) return;

        try {
            setSelectedTopic(topicId);
            setLoading(true);

            const response = await fetch(`${server_url}/short-qas/${topicId}/indices`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });

            if (!response.ok) {
                console.error("Error fetching indices:", await response.json());
                return;
            }

            const data = await response.json();
            setQuestionIndices(data.indices);
            setCurrentIndex(0);

            setQuizStarted(true);
            setQuizEnded(false);
            if (data.indices.length > 0) {
                fetchAudioQuestion(topicId, 0);
            }
        } catch (error) {
            console.error("Error fetching question indices:", error);
        }

        setLoading(false);
    };

    const startCountdown = () => {
        let count = 3;
        setCountdown(count);

        const countdownInterval = setInterval(() => {
            count -= 1;
            setCountdown(count);
            if (count === 0) {
                clearInterval(countdownInterval);
                setCountdown(null);
                startRecording();  // ✅ Automatically start recording
            }
        }, 1000);
    };

    // Fetch Audio Question by Index
    const fetchAudioQuestion = async (topicId, index) => {
        setIsLoadingQuestion(true);
        setFeedback(null);
        if (!topicId || index === undefined) return;

        try {
            const response = await fetch(`${server_url}/short-qas/${topicId}/${index}`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });

            if (!response.ok) {
                console.error("Error fetching audio:", await response.json());
                return;
            }

            const audioBlob = await response.blob();
            const audioURL = URL.createObjectURL(audioBlob);
            setAudioSrc(audioURL);
            setAudioPlaying(true); // ✅ Start animation

            if (audioRef.current) {
                audioRef.current.src = audioURL;

                // Ensure the audio loads before playing
                audioRef.current.oncanplaythrough = async () => {
                    try {
                        await audioRef.current.play();
                        setAudioPlaying(true);
                    } catch (error) {
                        console.warn("Autoplay prevented:", error);
                    }
                };

                // ✅ Stop animation when audio ends & start countdown
                audioRef.current.onended = () => {
                    setAudioPlaying(false);
                    startCountdownToRecording(); // ✅ Start countdown after audio finishes
                };
            }
        } catch
        (error) {
            console.error("Network error fetching audio question:", error);
        }
    };

    const [countdown, setCountdown] = useState(null);

    const startCountdownToRecording = () => {
        let count = 3;
        setCountdown(count);

        const countdownInterval = setInterval(() => {
            count -= 1;
            setCountdown(count);

            if (count === 0) {
                clearInterval(countdownInterval);
                setCountdown(null);

                // ✅ Ensure state updates correctly before starting recording
                setTimeout(() => {
                    setRecording(true);
                    startRecording();
                }, 300); // Small delay to allow state to update
            }
        }, 1000);
    };


    // Start Recording
    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: "audio/webm" });
            audioChunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorderRef.current.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
                const newAudioFile = new File([audioBlob], "recorded_audio.webm", { type: "audio/webm" });

                console.log("✅ Recorded file:", newAudioFile); // Debugging

                setAudioFile(newAudioFile); // ✅ Ensure audio file is set
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);
        } catch (error) {
            console.error("Error starting recording:", error);
            message.error("Microphone access required!");
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
                const newAudioFile = new File([audioBlob], "recorded_audio.webm", { type: "audio/webm" });

                console.log("✅ Audio file recorded:", newAudioFile); // Debugging

                setAudioFile(newAudioFile);
                setRecording(false);
            };

            mediaRecorderRef.current.stop();
        }
    };


    const nextQuestion = async () => {
        if (currentIndex + 1 < questionIndices.length) {
            setCurrentIndex(prev => prev + 1);
            setFeedback(null);
            setAudioFile(null);  // Reset recorded audio
            setCountdown(null);  // Reset countdown
            setAudioPlaying(false); // Reset animation
            setIsSubmitting(false);
            fetchAudioQuestion(selectedTopic, currentIndex + 1);
        } else {
            fetchFinalReport();
        }
    };


    const fetchFinalReport = async () => {
        try {
            const response = await fetch(`${server_url}/short-qas/${selectedTopic}/report`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });

            if (!response.ok) {
                console.error("Error fetching report:", await response.json());
                return;
            }

            const reportData = await response.json();
            setReport(reportData);
        } catch (error) {
            console.error("Error fetching final report:", error);
        }
    };

    // const submitAnswer = async () => {
    //     if (isRecording) {
    //         console.log("⏳ Stopping recording before submitting...");
    //         await stopRecording();
    //
    //         // Wait for state to update
    //         setTimeout(() => {
    //             if (!audioFile) {
    //                 message.error("Please record an answer first.");
    //                 return;
    //             }
    //             handleSubmit(); // Call the actual submission function
    //         }, 1000);  // Small delay to ensure state is updated
    //     } else {
    //         handleSubmit();
    //     }
    // };


    // Submit Answer
    const submitAnswer = async () => {
        if (!audioFile) {
            message.error("Please record an answer first.");
            return;
        }

        setIsSubmitting(true);
        setFeedback(null);
        const formData = new FormData();
        formData.append("question_index", currentIndex);

        // Convert to MP3 or WAV if needed
        const convertedAudio = new File([audioFile], "recorded_audio.mp3", { type: "audio/mp3" }); // 🔥 Convert filename
        formData.append("user_audio", convertedAudio); // 🔥 Ensuring correct format

        try {
            const response = await fetch(`${server_url}/short-qas/${selectedTopic}/submit`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: formData,
            });

            if (!response.ok) {
                console.error("Error submitting answer:", await response.json());
                return;
            }

            const data = await response.json();
            setFeedback(data);
            setIsSubmitting(false);

            // // ✅ Ensure feedback is an array before updating state
            // if (Array.isArray(data.feedback)) {
            //     setFeedback([...data.feedback]);
            // } else {
            //     console.warn("Unexpected feedback format:", data.feedback);
            //     setFeedback([]); // ✅ Default to empty array if feedback is not iterable
            // }
            //
            // const nextIndex = currentIndex + 1;
            // if (nextIndex < questionIndices.length) {
            //     setCurrentIndex(nextIndex);
            //     fetchAudioQuestion(selectedTopic, nextIndex);
            // } else {
            //     generateReport();
            //     setQuizEnded(true);
            //     setQuizStarted(false);
            // }
        } catch (error) {
            console.error("Error submitting answer:", error);
        }
        setIsSubmitting(false);
    };


    // Generate Final Report
    const generateReport = async () => {
        try {
            const response = await fetch(`${server_url}/short-qas/${selectedTopic}/report`);
            const data = await response.json();
            setReport(data);
        } catch (error) {
            console.error("Error fetching report:", error);
        }
    };

    // const restartAudio = () => {
    //     if (audioRef.current && audioSrc) {
    //         audioRef.current.src = audioSrc; // Ensure source is set
    //         audioRef.current.currentTime = 0;
    //         audioRef.current.play();
    //         setAudioPlaying(true);
    //     }
    // };
    const restartAudio = () => {
        if (audioRef.current && audioSrc) {
            audioRef.current.src = audioSrc;
            audioRef.current.load(); // Ensure it reloads properly
            audioRef.current.oncanplaythrough = () => {
                audioRef.current.play();
                setAudioPlaying(true);
            };
        }
    };

    const resetQuiz = () => {
        setQuizEnded(false);
        setQuizStarted(false);
        setSelectedTopic(null);
        setSelectedBook(null);
        setFeedback([]);
        setAudioFile(null);
        setQuestionIndices([]);
        setAudioSrc(null);
    };


    return (
        <Layout>
            {/*<div className="container mx-auto py-6">*/}
            {/*    {!quizStarted && !quizEnded && (*/}
            {/*        <>*/}
            {/*            <h2 className="text-4xl font-bold font-gotham text-gray-800 dark:text-white mb-6">Oral*/}
            {/*                Assessment</h2>*/}

            {/*            /!* Book Cards *!/*/}
            {/*            <Row gutter={[16, 16]}>*/}
            {/*                {books.map((book) => (*/}
            {/*                    <Col xs={24} sm={12} md={8} lg={6} key={book.book_id}>*/}
            {/*                        <Card hoverable className="shadow-lg rounded-xl">*/}
            {/*                            <Card.Meta*/}
            {/*                                title={<h3 className="text-xl font-gotham font-semibold">{book.name}</h3>}/>*/}
            {/*                            <Button*/}
            {/*                                type="primary"*/}
            {/*                                className="mt-4 w-full bg-yellow-500 hover:bg-yellow-600 text-black"*/}
            {/*                                onClick={() => {*/}
            {/*                                    setSelectedBook(book);*/}
            {/*                                    fetchTopics(book.book_id);*/}
            {/*                                }}*/}
            {/*                            >*/}
            {/*                                Select Book*/}
            {/*                            </Button>*/}
            {/*                        </Card>*/}
            {/*                    </Col>*/}
            {/*                ))}*/}
            {/*            </Row>*/}

            {/*            /!* Topics *!/*/}
            {/*            {selectedBook && (*/}
            {/*                <Row gutter={[16, 16]} className="mt-6">*/}
            {/*                    {topics.map((topic) => (*/}
            {/*                        <Col key={topic.topic_id} xs={24} sm={12} md={8}>*/}
            {/*                            <Button*/}
            {/*                                type="default"*/}
            {/*                                className="w-full bg-yellow-500 text-black p-2 rounded-md"*/}
            {/*                                onClick={() => fetchQuestionIndices(topic.topic_id)}*/}
            {/*                            >*/}
            {/*                                {topic.name}*/}
            {/*                            </Button>*/}
            {/*                        </Col>*/}
            {/*                    ))}*/}
            {/*                </Row>*/}
            {/*            )}*/}
            {/*        </>*/}
            {/*    )}*/}

            {/*{!quizStarted && !quizEnded && (*/}
            {/*    <>*/}
            {/*        <h2 className="text-3xl font-bold mb-4">Select a Book and Topic</h2>*/}
            {/*        <Row gutter={[16, 16]}>*/}
            {/*            {books.map((book) => (*/}
            {/*                <Col key={book.book_id} xs={24} sm={12} md={8}>*/}
            {/*                    <Card hoverable className="shadow-lg">*/}
            {/*                        <Card.Meta title={book.name}/>*/}
            {/*                        <Button*/}
            {/*                            type="primary"*/}
            {/*                            onClick={() => {*/}
            {/*                                setSelectedBook(book);*/}
            {/*                                fetchTopics(book.book_id);*/}
            {/*                            }}*/}
            {/*                        >*/}
            {/*                            Select Book*/}
            {/*                        </Button>*/}
            {/*                    </Card>*/}
            {/*                </Col>*/}
            {/*            ))}*/}
            {/*        </Row>*/}
            {/*        {selectedBook && (*/}
            {/*            <Row gutter={[16, 16]} className="mt-6">*/}
            {/*                {topics.map((topic) => (*/}
            {/*                    <Col key={topic.topic_id} xs={24} sm={12} md={8}>*/}
            {/*                        <Button*/}
            {/*                            type="primary"*/}
            {/*                            onClick={() => fetchQuestionIndices(topic.topic_id)}*/}
            {/*                        >*/}
            {/*                            {topic.name}*/}
            {/*                        </Button>*/}
            {/*                    </Col>*/}
            {/*                ))}*/}
            {/*            </Row>*/}
            {/*        )}*/}
            {/*    </>*/}
            {/*)}*/}

            {/* Audio Question */}
            {/* Audio Question Modal */}
            {/* Audio Question Modal */}
            {/*{quizStarted && !quizEnded && (*/}
            {/*    <div className="quiz-container">*/}
            {/*        <h1 className="heading-h2">Oral Assessment</h1>*/}
            {/*        <div className="quiz-header">*/}
            {/*            <h3>Question {currentIndex + 1} of {questionIndices.length}</h3>*/}

            {/* Audio Visualizer Animation (Shows when playing audio) */}
            {/*    <div className="audio-visualizer-container">*/}
            {/*        {audioPlaying ? (*/}
            {/*            <>*/}
            {/*                <div className={`audio-visualizer-1 ${audioPlaying ? "playing" : ""}`}></div>*/}
            {/*                <div className={`audio-visualizer-2 ${audioPlaying ? "playing" : ""}`}></div>*/}
            {/*                <div className={`audio-visualizer-3 ${audioPlaying ? "playing" : ""}`}></div>*/}
            {/*            </>*/}
            {/*        ) : (*/}
            {/*            <div className="idle-fat-dots">*/}
            {/*                <span>●</span> <span>●</span> <span>●</span> /!* ✅ Fat Dots *!/*/}
            {/*            </div>  // ✅ Show three dots when idle*/}
            {/*        )}*/}
            {/*    </div>*/}

            {/*    <audio ref={audioRef}/>*/}
            {/*    <Button onClick={restartAudio}><RedoOutlined/> Listen Again</Button>*/}
            {/*</div>*/}

            {/*<div className="quiz-content">*/}
            {/* Fixed div for recording animation */}
            {/*<div className="fixed-recording-container">*/}
            {/*    {countdown !== null ? (*/}
            {/*        <div className="countdown-animation">*/}
            {/*            <h2>{countdown}</h2>*/}
            {/*        </div>*/}
            {/*    ) : (*/}
            {/*        <div className="recording-visualizer-container">*/}
            {/*            <div className={`recording-visualizer-1 ${recording ? "recording" : ""}`}></div>*/}
            {/*            <div className={`recording-visualizer-2 ${recording ? "recording" : ""}`}></div>*/}
            {/*            <div className={`recording-visualizer-3 ${recording ? "recording" : ""}`}></div>*/}
            {/*        </div>*/}
            {/*    )}*/}
            {/*</div>*/}
            {/*<div className="fixed-recording-container">*/}
            {/*    {countdown !== null ? (*/}
            {/*        // ✅ Countdown with fat dots*/}
            {/*        <div className="countdown-animation">*/}
            {/*            <h2>{countdown}</h2>*/}
            {/*        </div>*/}
            {/*    ) : (*/}
            {/*        // ✅ Recording animation with fat dots when idle*/}
            {/*        <div className="recording-visualizer-container">*/}
            {/*            {recording ? (*/}
            {/*                <>*/}
            {/*                    <div className="recording-visualizer-1 recording"></div>*/}
            {/*                    <div className="recording-visualizer-2 recording"></div>*/}
            {/*                    <div className="recording-visualizer-3 recording"></div>*/}
            {/*                </>*/}
            {/*            ) : (*/}
            {/*                <div className="idle-fat-dots">*/}
            {/*                    <span>●</span> <span>●</span> <span>●</span>*/}
            {/*                </div>*/}
            {/*            )}*/}
            {/*        </div>*/}
            {/*    )}*/}
            {/*</div>*/}


            {/* Move Next button outside fixed div */}
            {/*<Button type="primary" onClick={nextQuestion} className="next-button">*/}
            {/*    Next*/}
            {/*</Button>*/}
            {/*</div>*/}
            {/* "Next" button stays separate */}
            {/*<Button type="primary" onClick={nextQuestion} className="next-button">*/}
            {/*    Next*/}
            {/*</Button>*/}
            {/*{feedback && !isSubmitting && (*/}
            {/*    <Button type="primary" onClick={nextQuestion} className="next-button">*/}
            {/*        Next*/}
            {/*    </Button>*/}
            {/*)}*/}


            {/*<div className="quiz-header">*/}
            {/*/! * Recording Animation (Shows when recording is active) *!/*/}
            {/*    /!*<div className="recording-visualizer-container">*!/*/}
            {/*    /!*    <div className={`recording-visualizer-1 ${recording ? "recording" : ""}`}></div>*!/*/}
            {/*    /!*    <div className={`recording-visualizer-2 ${recording ? "recording" : ""}`}></div>*!/*/}
            {/*    /!*    <div className={`recording-visualizer-3 ${recording ? "recording" : ""}`}></div>*!/*/}
            {/*    /!*</div>*!/*/}

            {/*    /!*<Button*!/*/}
            {/*    /!*    type="primary"*!/*/}
            {/*    /!*    onClick={recording ? stopRecording : startRecording}*!/*/}
            {/*    /!*    className="record-btn"*!/*/}
            {/*    /!*>*!/*/}
            {/*    /!*    {recording ? "Stop Recording" : <AudioOutlined/>} Record Answer*!/*/}
            {/*    /!*</Button>*!/*/}

            {/*    /!* Countdown Animation *!/*/}
            {/*    {countdown !== null ? (*/}
            {/*        <div className="countdown-animation">*/}
            {/*            <h2>{countdown}</h2>*/}
            {/*        </div>*/}
            {/*    ) : (*/}
            {/*        <div className="recording-visualizer-container">*/}
            {/*            <div className={`recording-visualizer-1 ${recording ? "recording" : ""}`}></div>*/}
            {/*            <div className={`recording-visualizer-2 ${recording ? "recording" : ""}`}></div>*/}
            {/*            <div className={`recording-visualizer-3 ${recording ? "recording" : ""}`}></div>*/}
            {/*        </div>*/}
            {/*    )}*/}

            {/*</div>*/}
            {/*/!*<Button type="primary" onClick={submitAnswer} disabled={isSubmitting}>*!/*/}
            {/*/!*    Next*!/*/}
            {/*/!*</Button>*!/*/}
            {/*/!* Next Button to move forward and auto-stop recording *!/*/}
            {/*<Button type="primary" onClick={nextQuestion}>*/}
            {/*    Next*/}
            {/*</Button>*/}
            {/*    </div>*/}
            {/*)}*/}

            {/*{audioSrc && (*/}
            {/*    <Modal*/}
            {/*        open={quizVisible}*/}
            {/*        onCancel={() => setQuizVisible(false)}*/}
            {/*        footer={null} width={600} centered>*/}
            {/*        <h3>Question {currentIndex + 1} out of {questionIndices.length}</h3>*/}

            {/*        <Divider/>*/}

            {/*        /!* 🎵 Audio Visualizer Container *!/*/}
            {/*        <div className="visualizer-container">*/}
            {/*            <div className={`audio-visualizer-1 ${audioPlaying ? "playing" : ""}`}/>*/}
            {/*            <div className={`audio-visualizer-2 ${audioPlaying ? "playing" : ""}`}/>*/}
            {/*            <div className={`audio-visualizer-3 ${audioPlaying ? "playing" : ""}`}/>*/}
            {/*        </div>*/}

            {/*        <audio ref={audioRef} onEnded={() => setAudioPlaying(false)}/>*/}

            {/*        <Button onClick={restartAudio} className="mt-4">*/}
            {/*            <RedoOutlined/> Restart Audio*/}
            {/*        </Button>*/}

            {/*        /!* 🎤 Recording Animation *!/*/}
            {/*        <div className="visualizer-container">*/}
            {/*            <div className={`recording-visualizer-1 ${recording ? "recording" : ""}`}/>*/}
            {/*            <div className={`recording-visualizer-2 ${recording ? "recording" : ""}`}/>*/}
            {/*            <div className={`recording-visualizer-3 ${recording ? "recording" : ""}`}/>*/}
            {/*        </div>*/}

            {/*        <Button onClick={recording ? stopRecording : startRecording} type="primary" className="mt-4">*/}
            {/*            {recording ? "Stop Recording" : <AudioOutlined/>} Record Answer*/}
            {/*        </Button>*/}

            {/*        <Button onClick={submitAnswer} loading={isSubmitting}>*/}
            {/*            Submit Answer*/}
            {/*        </Button>*/}

            {/*        /!* Feedback Section *!/*/}
            {/*        {feedback && (*/}
            {/*            <div className="mt-4 p-2 bg-gray-200 rounded">*/}
            {/*                <h4>Feedback:</h4>*/}
            {/*                <p>{feedback.explanation}</p>*/}
            {/*            </div>*/}
            {/*        )}*/}
            {/*    </Modal>*/}
            {/*)}*/}

            {/*    {report && (*/}
            {/*        <Modal open={true} footer={null} onCancel={() => setReport(null)}>*/}
            {/*            <h2>Assessment Report</h2>*/}
            {/*            <p>Correct Answers: {report.num_correct} / {report.total_questions}</p>*/}
            {/*            <p>Score: {report.avg_score}%</p>*/}
            {/*        </Modal>*/}
            {/*    )}*/}

            {/*    {quizStarted && !quizEnded && (*/}
            {/*        <>*/}
            {/*            /!* Show Submit Button if feedback is not yet received *!/*/}
            {/*            {!feedback ? (*/}
            {/*                <div style={{display: "flex", gap: "1px", fontFamily: "Gotham, sans-serif",}}>*/}
            {/*                    <Button type="primary" onClick={stopRecording} className="next-button"*/}
            {/*                            style={{*/}
            {/*                                backgroundColor: "red",*/}
            {/*                                borderColor: "red",*/}
            {/*                                fontFamily: "Gotham, sans-serif",*/}
            {/*                            }}>*/}
            {/*                        Stop recording*/}
            {/*                    </Button>*/}
            {/*                    <Button type="primary" onClick={submitAnswer} loading={isSubmitting}*/}
            {/*                            className="next-button">*/}
            {/*                        {isSubmitting ? "Submitting..." : "Submit"}*/}
            {/*                    </Button>*/}
            {/*                </div>*/}

            {/*            ) : (*/}
            {/*                <>*/}
            {/*                    /!* Show Feedback Card ONLY when feedback is received *!/*/}
            {/*                    <Card className="feedback-card">*/}
            {/*                        <p><strong>Your Answer:</strong> {feedback?.user_answer || "N/A"}</p>*/}
            {/*                        <p><strong>Correct Answer:</strong> {feedback?.correct_answer || "N/A"}</p>*/}
            {/*                        <p>*/}
            {/*                            <strong>Explanation:</strong> {feedback?.explanation || "No explanation available."}*/}
            {/*                        </p>*/}
            {/*                    </Card>*/}
            {/*                    <Button type="primary" onClick={nextQuestion} className="next-button">*/}
            {/*                        Next*/}
            {/*                    </Button>*/}
            {/*                </>*/}
            {/*            )}*/}
            {/*        </>*/}
            {/*    )}*/}


            {/*    {quizEnded && (*/}
            {/*        <Modal open={quizEnded} footer={null} onCancel={resetQuiz}>*/}
            {/*            <h2>Quiz Completed!</h2>*/}
            {/*            <Progress percent={report?.avg_score || 0}/>*/}
            {/*            <Button type="primary" onClick={() => fetchQuestionIndices(selectedTopic)}>*/}
            {/*                Retake Quiz*/}
            {/*            </Button>*/}
            {/*            <Button onClick={resetQuiz}>Select Another Topic</Button>*/}
            {/*        </Modal>*/}
            {/*    )}*/}

            {/*</div>*/}
            <div className="min-h-screen font-[poppins] py-10 sm:px-6">
                <div className="max-w-7xl mx-auto">
                    <AnimatePresence mode="wait">
                        {!quizStarted && !quizEnded ? (
                            <motion.div
                                key="selection"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -30 }}
                                transition={{ duration: 0.5 }}
                                className="space-y-8"
                            >
                                {/*<div className="flex items-center space-x-3 mb-8">*/}
                                {/*    <Book className="h-8 w-8 text-[#D67C2C]"/>*/}
                                {/*    <h1 className="text-4xl font-bold text-[#D67C2C]">Oral Assessment</h1>*/}
                                {/*</div>*/}

                                {/* Header */}
                                <div
                                    className=" top-0 z-10 backdrop-blur-sm py-6 mb-6">
                                    <div className="flex items-center justify-center">

                                        <h1 className="text-4xl font-bold text-[#D67C2C] text-center">Oral Assessment</h1>

                                    </div>
                                </div>

                                {loading ? (
                                    <div className="flex justify-center py-10">
                                        <div className="spinner"></div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="space-y-6">
                                            <h2 className="text-2xl font-semibold text-gray-800">Select a Book</h2>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                                {books.map((book) => (
                                                    <motion.div
                                                        key={book.book_id}
                                                        whileHover={{ scale: 1.03 }}
                                                        whileTap={{ scale: 0.98 }}
                                                        onClick={() => {
                                                            setSelectedBook(book);
                                                            fetchTopics(book.book_id);
                                                        }}
                                                        className={`cursor-pointer rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 ${selectedBook?.book_id === book.book_id
                                                                ? "ring-2 ring-[#D67C2C] bg-blue-50"
                                                                : "bg-white"
                                                            }`}
                                                    >
                                                        <div
                                                            className="h-3 bg-[#D67C2C]"></div>
                                                        <div className="p-6">
                                                            <h3 className="text-xl font-semibold text-gray-800 mb-4">{book.name}</h3>
                                                            <button
                                                                className="w-full py-3 px-4 bg-[#D67C2C] text-white font-medium rounded-lg hover:bg-orange-400transition-colors duration-300 flex items-center justify-center"
                                                            >
                                                                <span>Select Book</span>
                                                            </button>
                                                        </div>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </div>

                                        {selectedBook && topics.length > 0 && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: "auto" }}
                                                className="space-y-6"
                                            >
                                                <h2 className="text-2xl font-semibold text-gray-800">Select a Topic</h2>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                                    {topics.map((topic) => (
                                                        <motion.button
                                                            key={topic.topic_id}
                                                            whileHover={{ scale: 1.03 }}
                                                            whileTap={{ scale: 0.98 }}
                                                            onClick={() => fetchQuestionIndices(topic.topic_id)}
                                                            className="p-4 bg-white shadow-md rounded-lg text-left hover:shadow-lg transition-all duration-300 hover:bg-blue-50 border border-gray-200"
                                                        >
                                                            <h3 className="text-lg font-medium text-gray-700">{topic.name}</h3>
                                                            <div className="flex justify-end mt-3">
                                                                <Zap className="h-5 w-5 text-[#D67C2C]" />
                                                            </div>
                                                        </motion.button>
                                                    ))}
                                                </div>
                                            </motion.div>
                                        )}
                                    </>
                                )}
                            </motion.div>
                        ) : quizStarted && !quizEnded ? (
                            <motion.div
                                key="quiz"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -30 }}
                                transition={{ duration: 0.5 }}
                                className="bg-white rounded-2xl shadow-xl overflow-hidden"
                            >
                                {/* Progress bar */}
                                <div className="w-full h-3 bg-gray-100">
                                    <motion.div
                                        initial={{ width: '0%' }}
                                        animate={{ width: `${((currentIndex + 1) / questionIndices.length) * 100}%` }}
                                        transition={{ duration: 0.5 }}
                                        className="h-full bg-[#D67C2C]"
                                    ></motion.div>
                                </div>

                                <div className="p-6 md:p-8">
                                    {/* Header */}
                                    <div className="flex justify-between items-center mb-8">
                                        <h2 className="text-2xl font-bold text-gray-800">Oral Assessment</h2>
                                        <div className="px-4 py-2 bg-blue-50 rounded-full text-[#D67C2C] font-medium">
                                            Question {currentIndex + 1} of {questionIndices.length}
                                        </div>
                                    </div>

                                    {/* Audio player */}
                                    <div className="mb-10 bg-gray-50 rounded-xl p-6">
                                        <div className="flex justify-between items-center mb-4">
                                            <div className="flex items-center space-x-2">
                                                <Volume2
                                                    className={`h-5 w-5 ${audioPlaying ? "text-[#D67C2C]" : "text-gray-400"}`} />
                                                <span className="text-lg font-medium text-gray-700">Listen to the question</span>
                                            </div>
                                            <button
                                                onClick={restartAudio}
                                                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                            >
                                                <RefreshCw className="h-4 w-4 text-gray-600" />
                                                <span>Listen Again</span>
                                            </button>
                                        </div>

                                        {/* Audio visualizer */}
                                        <div className="flex justify-center items-center h-20">
                                            {audioPlaying ? (
                                                <div className="flex items-end space-x-1">
                                                    {[...Array(6)].map((_, i) => (
                                                        <motion.div
                                                            key={i}
                                                            animate={{
                                                                height: [
                                                                    `${20 + Math.random() * 40}px`,
                                                                    `${60 + Math.random() * 40}px`,
                                                                    `${20 + Math.random() * 40}px`
                                                                ]
                                                            }}
                                                            transition={{
                                                                repeat: Infinity,
                                                                duration: 1.4,
                                                                delay: i * 0.1
                                                            }}
                                                            className="w-3 bg-[#D67C2C] rounded-t-full"
                                                        ></motion.div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="flex space-x-2">
                                                    {[...Array(3)].map((_, i) => (
                                                        <div key={i} className="w-3 h-3 rounded-full bg-gray-300"></div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <audio ref={audioRef} className="hidden" />
                                    </div>

                                    {/* Recording section */}
                                    <div className="mb-8">
                                        <div className="flex justify-center items-center h-24">
                                            {countdown !== null ? (
                                                <motion.div
                                                    initial={{ scale: 0.8, opacity: 0 }}
                                                    animate={{ scale: 1, opacity: 1 }}
                                                    className="flex flex-col items-center"
                                                >
                                                    <span
                                                        className="text-4xl font-bold text-[#D67C2C]">{countdown}</span>
                                                    <span
                                                        className="text-gray-500 mt-2">Recording will start soon...</span>
                                                </motion.div>
                                            ) : recording ? (
                                                <div className="flex flex-col items-center">
                                                    <div className="flex items-end space-x-1 mb-4">
                                                        {[...Array(4)].map((_, i) => (
                                                            <motion.div
                                                                key={i}
                                                                animate={{
                                                                    height: [
                                                                        `${20 + Math.random() * 30}px`,
                                                                        `${50 + Math.random() * 30}px`,
                                                                        `${20 + Math.random() * 30}px`
                                                                    ]
                                                                }}
                                                                transition={{
                                                                    repeat: Infinity,
                                                                    duration: 1,
                                                                    delay: i * 0.15
                                                                }}
                                                                className="w-3 bg-gradient-to-t from-red-500 to-pink-500 rounded-t-full"
                                                            ></motion.div>
                                                        ))}
                                                    </div>
                                                    <span className="text-red-500 font-medium animate-pulse">Recording in progress...</span>
                                                </div>
                                            ) : (
                                                <div className="text-gray-400">
                                                    {feedback ? "Answer submitted" : "Waiting for audio..."}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Buttons */}
                                    {!feedback ? (
                                        <div className="flex justify-center space-x-4">
                                            {recording && (
                                                <motion.button
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    onClick={stopRecording}
                                                    className="px-6 py-3 bg-red-600 text-white font-medium rounded-lg flex items-center space-x-2 hover:bg-red-700 transition-colors"
                                                >
                                                    <Square className="h-4 w-4" />
                                                    <span>Stop Recording</span>
                                                </motion.button>
                                            )}
                                            <motion.button
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                onClick={submitAnswer}
                                                disabled={isSubmitting || !audioFile}
                                                className={`px-6 py-3 ${audioFile
                                                        ? "bg-[#D67C2C] hover:bg-orange-400"
                                                        : "bg-gray-400 cursor-not-allowed"
                                                    } text-white font-medium rounded-lg flex items-center space-x-2 transition-colors`}
                                            >
                                                {isSubmitting ? (
                                                    <div
                                                        className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                ) : (
                                                    <Play className="h-4 w-4" />
                                                )}
                                                <span>{isSubmitting ? "Submitting..." : "Submit Answer"}</span>
                                            </motion.button>
                                        </div>
                                    ) : (
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="space-y-6"
                                        >
                                            {/* Feedback card */}
                                            <div className="bg-blue-50 border border-blue-100 rounded-xl p-6">
                                                <div className="flex items-start space-x-4 mb-4">
                                                    <div className="mt-1">
                                                        {feedback.explanation.includes("correct") ? (
                                                            <CheckCircle className="h-6 w-6 text-green-500" />
                                                        ) : (
                                                            <XCircle className="h-6 w-6 text-red-500" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <h3 className="text-xl font-semibold text-gray-800 mb-2">
                                                            {feedback.explanation.includes("correct") ? "Great job!" : "Keep practicing!"}
                                                        </h3>
                                                        <p className="text-gray-600 mb-4">{feedback.explanation}</p>

                                                        <div
                                                            className="space-y-3 bg-white p-4 rounded-lg border border-gray-200">
                                                            <div>
                                                                <h4 className="font-medium text-gray-700 mb-1">Your
                                                                    Answer:</h4>
                                                                <p className="text-gray-600">{feedback.user_answer || "No answer detected"}</p>
                                                            </div>
                                                            <div>
                                                                <h4 className="font-medium text-gray-700 mb-1">Correct
                                                                    Answer:</h4>
                                                                <p className="text-gray-600">{feedback.correct_answer}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex justify-center">
                                                <motion.button
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    onClick={nextQuestion}
                                                    className="px-8 py-3 bg-[#D67C2C] text-white font-medium rounded-lg flex items-center space-x-2 hover:bg-orange-400 transition-colors shadow-md"
                                                >
                                                    <span>Next Question</span>
                                                </motion.button>
                                            </div>
                                        </motion.div>
                                    )}
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="report"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -30 }}
                                transition={{ duration: 0.5 }}
                                className="bg-white rounded-2xl shadow-xl p-8 max-w-3xl mx-auto"
                            >
                                <div className="text-center mb-8">
                                    <h2 className="text-3xl font-bold text-gray-800 mb-2">Assessment Complete!</h2>
                                    <p className="text-gray-600">Here's your performance summary</p>
                                </div>

                                {report && (
                                    <>
                                        <div className="space-y-6">
                                            <div className="flex justify-between items-center">
                                                <div>
                                                    <h3 className="text-xl font-semibold text-gray-700">Score</h3>
                                                    <p className="text-4xl font-bold text-[#D67C2C]">{report.avg_score}%</p>
                                                </div>
                                                <div>
                                                    <h3 className="text-xl font-semibold text-gray-700">Correct
                                                        Answers</h3>
                                                    <p className="text-4xl font-bold text-green-600">
                                                        {report.num_correct} / {report.total_questions}
                                                    </p>
                                                </div>
                                            </div>

                                            <div>
                                                <h3 className="text-xl font-semibold text-gray-700 mb-2">Progress</h3>
                                                <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
                                                    <motion.div
                                                        initial={{ width: 0 }}
                                                        animate={{ width: `${report.avg_score}%` }}
                                                        transition={{ duration: 1, delay: 0.3 }}
                                                        className={`h-full ${report.avg_score >= 80
                                                                ? "bg-green-500"
                                                                : report.avg_score >= 60
                                                                    ? "bg-[#D67C2C]"
                                                                    : "bg-red-500"
                                                            }`}
                                                    ></motion.div>
                                                </div>
                                            </div>

                                            <div className="mt-10 flex justify-center space-x-4">
                                                <motion.button
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    onClick={() => fetchQuestionIndices(selectedTopic)}
                                                    className="px-6 py-3 bg-[#D67C2C] text-white font-medium rounded-lg hover:bg-orange-400 transition-colors"
                                                >
                                                    Retake Assessment
                                                </motion.button>
                                                <motion.button
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    onClick={resetQuiz}
                                                    className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                                                >
                                                    Choose Another Topic
                                                </motion.button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Loading spinner - global */}
                <style jsx>{`
                    .spinner {
                        width: 40px;
                        height: 40px;
                        border-radius: 50%;
                        border: 3px solid rgba(59, 130, 246, 0.2);
                        border-top-color: rgba(59, 130, 246, 1);
                        animation: spin 1s linear infinite;
                    }

                    @keyframes spin {
                        to {
                            transform: rotate(360deg);
                        }
                    }
                `}</style>
            </div>
        </Layout>
    );
};

export default OralAssessment;
